import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  ArrowRightIcon,
  BookUserIcon,
  BuildingIcon,
  ChevronDownIcon,
  EyeIcon,
  InfoIcon,
  MoveDiagonalIcon,
  PlusIcon,
  SearchIcon,
  UsersIcon
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput
} from '@/components/ui/input-group';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { CreateAccountDialog } from '@/pages/firm/crm-accounts/dialogs/CreateAccountDialog';
import { CreateContactDialog } from '@/pages/firm/crm-contacts/dialogs/CreateContactDialog';

const GET_CONTACTS_SUMMARY = gql`
  query GetContactsSummary($first: Int, $name: String) {
    contacts(first: $first, name: $name) {
      edges {
        node {
          id
          name
          isCompany
        }
      }
      totalCount
    }
  }
`;

const GET_ACCOUNTS_SUMMARY = gql`
  query GetAccountsSummary($first: Int, $name: String) {
    accounts(first: $first, name: $name) {
      edges {
        node {
          id
          name
        }
      }
      totalCount
    }
  }
`;

const GET_OPPORTUNITIES_SUMMARY = gql`
  query GetOpportunitiesSummary(
    $first: Int
    $offset: Int
    $onlyActiveOpportunities: Boolean
    $name: String
  ) {
    opportunities(
      first: $first
      offset: $offset
      onlyActiveOpportunities: $onlyActiveOpportunities
      name: $name
    ) {
      edges {
        node {
          id
          name
        }
      }
      totalCount
    }
  }
`;

const GET_USER_SAVED_VIEWS = gql`
  query GetUserSavedViews {
    userSavedViews {
      edges {
        node {
          id
          name
          category
          departmentId
          departmentName
          targetModel
          recordCount
          createdByName
          createdAt
        }
      }
    }
  }
`;

interface Account {
  id: string;
  name: null | string;
}

interface AccountsData {
  accounts: {
    edges: { node: Account }[];
    totalCount: number;
  };
}

interface Contact {
  id: string;
  isCompany: boolean;
  name: null | string;
}

interface ContactsData {
  contacts: {
    edges: { node: Contact }[];
    totalCount: number;
  };
}

interface OpportunitiesData {
  opportunities: {
    edges: { node: Opportunity }[];
    totalCount: number;
  };
}

interface Opportunity {
  id: string;
  name: null | string;
}

interface View {
  category?: string;
  createdAt: string;
  createdByName: string;
  departmentId?: string;
  departmentName?: string;
  id: string;
  name: null | string;
  recordCount: number;
  targetModel: string;
}

interface ViewsData {
  userSavedViews: {
    edges: { node: View }[];
  };
}

const Home = () => {
  const navigate = useNavigate();

  const [contactsSearch, setContactsSearch] = useState('');
  const [accountsSearch, setAccountsSearch] = useState('');
  const [opportunitiesSearch, setOpportunitiesSearch] = useState('');
  const [viewsSearch, setViewsSearch] = useState('');
  const [expandedViewDepartments, setExpandedViewDepartments] = useState(
    () => new Set<string>()
  );
  const [expandedViewCategories, setExpandedViewCategories] = useState(
    () => new Set<string>()
  );
  const [isCreateContactDialogOpen, setIsCreateContactDialogOpen] =
    useState(false);
  const [isCreateAccountDialogOpen, setIsCreateAccountDialogOpen] =
    useState(false);

  const debouncedContactsSearch = useDebounce(contactsSearch, 300);
  const debouncedAccountsSearch = useDebounce(accountsSearch, 300);
  const debouncedOpportunitiesSearch = useDebounce(opportunitiesSearch, 300);
  const debouncedViewsSearch = useDebounce(viewsSearch, 300);

  const {
    data: contactsData,
    error: contactsError,
    loading: contactsLoading
  } = useQuery<ContactsData>(GET_CONTACTS_SUMMARY, {
    variables: { first: 3, name: debouncedContactsSearch || undefined }
  });

  const {
    data: accountsData,
    error: accountsError,
    loading: accountsLoading
  } = useQuery<AccountsData>(GET_ACCOUNTS_SUMMARY, {
    variables: { first: 3, name: debouncedAccountsSearch || undefined }
  });

  const {
    data: opportunitiesData,
    error: opportunitiesError,
    loading: opportunitiesLoading
  } = useQuery<OpportunitiesData>(GET_OPPORTUNITIES_SUMMARY, {
    variables: {
      first: 3,
      name: debouncedOpportunitiesSearch || undefined,
      offset: 0,
      onlyActiveOpportunities: true
    }
  });

  const {
    data: viewsData,
    error: viewsError,
    loading: viewsLoading
  } = useQuery<ViewsData>(GET_USER_SAVED_VIEWS);

  const toggleViewDepartment = (departmentKey: string) => {
    setExpandedViewDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(departmentKey)) {
        newSet.delete(departmentKey);
      } else {
        newSet.add(departmentKey);
      }
      return newSet;
    });
  };

  const toggleViewCategory = (categoryKey: string) => {
    setExpandedViewCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryKey)) {
        newSet.delete(categoryKey);
      } else {
        newSet.add(categoryKey);
      }
      return newSet;
    });
  };

  // Group views by department -> category
  // If no department, show category directly at top level
  const viewsGrouped =
    viewsData?.userSavedViews.edges
      .map(e => e.node)
      .filter(view =>
        debouncedViewsSearch
          ? view.name.toLowerCase().includes(debouncedViewsSearch.toLowerCase())
          : true
      )
      .reduce(
        (acc, view) => {
          if (view.departmentName) {
            // Has department: Department -> Category -> Views
            const deptKey = view.departmentName;
            if (!acc[deptKey]) {
              acc[deptKey] = {};
            }
            const catKey = view.category || '_no_category';
            if (!acc[deptKey][catKey]) {
              acc[deptKey][catKey] = [];
            }
            acc[deptKey][catKey].push(view);
          } else {
            // No department: Show category at top level
            const catKey = `_nodept_${view.category || '_no_category'}`;
            if (!acc[catKey]) {
              acc[catKey] = { _direct: [] };
            }
            acc[catKey]._direct.push(view);
          }
          return acc;
        },
        {} as Record<string, Record<string, View[]>>
      ) ?? {};

  const getInitials = (name: null | string | undefined) => {
    if (!name) {
      return '??';
    }
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (
    contactsLoading ||
    accountsLoading ||
    opportunitiesLoading ||
    viewsLoading
  ) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-auto bg-white p-6">
      <div className="mx-auto max-w-[1618px] space-y-6">
        {/* Top Section - Contacts, Accounts, Opportunities */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Contacts Card */}
          <div className="flex flex-col justify-between rounded-md border bg-white px-2.5 py-5">
            <div>
              <div className="mb-5 flex items-center justify-between">
                <div className="flex items-center gap-x-1.25">
                  <BookUserIcon className="size-6" />
                  <h2 className="text-lg font-semibold">Contacts</h2>
                </div>
                <div className="text-muted-foreground flex items-center gap-x-1">
                  <span className="text-sm font-medium">
                    {contactsData?.contacts.totalCount.toLocaleString() ?? '0'}
                  </span>
                  <InfoIcon className="size-4.5" />
                </div>
              </div>
              <Separator className="mb-5" />

              <InputGroup className="mb-4">
                <InputGroupAddon>
                  <SearchIcon />
                </InputGroupAddon>
                <InputGroupInput
                  onChange={e => {
                    setContactsSearch(e.target.value);
                  }}
                  placeholder="Search contacts..."
                  value={contactsSearch}
                />
              </InputGroup>

              <div className="mb-5 space-y-3">
                <div className="text-sm font-semibold">Recently Viewed</div>
                {contactsError ? (
                  <div className="text-sm text-red-500">
                    Error loading contacts
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {contactsData?.contacts.edges
                      .slice(0, 3)
                      .map(({ node }) => (
                        <Link
                          className="flex items-center gap-3 rounded-md p-2 hover:bg-gray-50"
                          key={node.id}
                          to={`/firm/crm-contacts/${node.id}`}
                        >
                          <Avatar className="size-6">
                            <AvatarFallback className="text-xs">
                              {getInitials(node.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-1 items-center gap-2 overflow-hidden">
                            {node.name && node.name.length > 30 ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="block cursor-default truncate text-sm">
                                    {node.name}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  {node.name}
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span className="block truncate text-sm">
                                {node.name || 'Unnamed Contact'}
                              </span>
                            )}
                            <Badge variant="secondary">Active</Badge>
                          </div>
                        </Link>
                      ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-x-2.5 border-t pt-5">
              <Button
                asChild
                className="min-w-0 flex-1"
                size="lg"
                variant="outline"
              >
                <Link to="/firm/crm-contacts">
                  <ArrowRightIcon className="shrink-0" />
                  <span className="truncate">Go to Contacts</span>
                </Link>
              </Button>
              <Button
                className="min-w-0 flex-1"
                onClick={() => {
                  setIsCreateContactDialogOpen(true);
                }}
                size="lg"
              >
                <PlusIcon className="shrink-0" />
                <span className="truncate">Add Contact</span>
              </Button>
            </div>
          </div>

          {/* Accounts Card */}
          <div className="flex flex-col justify-between rounded-md border bg-white px-2.5 py-5">
            <div>
              <div className="mb-5 flex items-center justify-between">
                <div className="flex items-center gap-x-1.25">
                  <BuildingIcon className="size-6" />
                  <h2 className="text-lg font-semibold">Accounts</h2>
                </div>
                <div className="text-muted-foreground flex items-center gap-x-1">
                  <span className="text-sm font-medium">
                    {accountsData?.accounts.totalCount.toLocaleString() ?? '0'}
                  </span>
                  <InfoIcon className="size-4.5" />
                </div>
              </div>
              <Separator className="mb-5" />

              <InputGroup className="mb-4">
                <InputGroupAddon>
                  <SearchIcon />
                </InputGroupAddon>
                <InputGroupInput
                  onChange={e => {
                    setAccountsSearch(e.target.value);
                  }}
                  placeholder="Search accounts..."
                  value={accountsSearch}
                />
              </InputGroup>

              <div className="mb-5 space-y-3">
                <div className="text-sm font-semibold">Recently Viewed</div>
                {accountsError ? (
                  <div className="text-sm text-red-500">
                    Error loading accounts
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {accountsData?.accounts.edges
                      .slice(0, 3)
                      .map(({ node }) => (
                        <Link
                          className="flex items-center gap-3 rounded-md p-2 hover:bg-gray-50"
                          key={node.id}
                          to={`/firm/crm-accounts/${node.id}`}
                        >
                          <Avatar className="size-6">
                            <AvatarFallback className="text-xs">
                              {getInitials(node.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-1 items-center justify-between overflow-hidden">
                            <div className="flex items-center gap-2 overflow-hidden">
                              {node.name && node.name.length > 20 ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="block cursor-default truncate text-sm font-medium">
                                      {node.name}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    {node.name}
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <span className="text-sm font-medium">
                                  {node.name || 'Unnamed Account'}
                                </span>
                              )}
                              <span className="block truncate text-sm text-gray-500">
                                Company Name
                              </span>
                            </div>
                            <div className="flex -space-x-2">
                              <Avatar className="size-6 border-2 border-white">
                                <AvatarFallback className="text-xs">
                                  JD
                                </AvatarFallback>
                              </Avatar>
                              <Avatar className="size-6 border-2 border-white">
                                <AvatarFallback className="text-xs">
                                  SM
                                </AvatarFallback>
                              </Avatar>
                            </div>
                          </div>
                        </Link>
                      ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-x-2.5 border-t pt-5">
              <Button
                asChild
                className="min-w-0 flex-1"
                size="lg"
                variant="outline"
              >
                <Link to="/firm/crm-accounts">
                  <ArrowRightIcon className="shrink-0" />
                  <span className="truncate">Go to Accounts</span>
                </Link>
              </Button>
              <Button
                className="min-w-0 flex-1"
                onClick={() => {
                  setIsCreateAccountDialogOpen(true);
                }}
                size="lg"
              >
                <PlusIcon className="shrink-0" />
                <span className="truncate">Add Account</span>
              </Button>
            </div>
          </div>

          {/* Opportunities Card */}
          <div className="flex flex-col justify-between rounded-md border bg-white px-2.5 py-5">
            <div>
              <div className="mb-5 flex items-center justify-between">
                <div className="flex items-center gap-x-1.25">
                  <UsersIcon className="size-6" />
                  <h2 className="text-lg font-semibold">Opportunities</h2>
                </div>
                <div className="text-muted-foreground flex items-center gap-x-1">
                  <span className="text-sm font-medium">
                    {opportunitiesData?.opportunities.totalCount.toLocaleString() ??
                      '0'}
                  </span>
                  <InfoIcon className="size-4.5" />
                </div>
              </div>
              <Separator className="mb-5" />

              <InputGroup className="mb-4">
                <InputGroupAddon>
                  <SearchIcon />
                </InputGroupAddon>
                <InputGroupInput
                  onChange={e => {
                    setOpportunitiesSearch(e.target.value);
                  }}
                  placeholder="Search opportunities..."
                  value={opportunitiesSearch}
                />
              </InputGroup>

              <div className="mb-5 space-y-3">
                <div className="text-sm font-semibold">Recently Viewed</div>
                {opportunitiesError ? (
                  <div className="text-sm text-red-500">
                    Error loading opportunities
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {opportunitiesData?.opportunities.edges
                      .slice(0, 3)
                      .map(({ node }) => (
                        <Link
                          className="flex items-center gap-3 rounded-md p-2 hover:bg-gray-50"
                          key={node.id}
                          to={`/investments/direct/${node.id}/overview`}
                        >
                          <Avatar className="size-6">
                            <AvatarFallback className="text-xs">
                              {getInitials(node.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-1 items-center gap-2 overflow-hidden">
                            {node.name && node.name.length > 30 ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="block cursor-default truncate text-sm">
                                    {node.name}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  {node.name}
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span className="block truncate text-sm">
                                {node.name || 'Unnamed Opportunity'}
                              </span>
                            )}
                            <Badge variant="secondary">Active</Badge>
                          </div>
                        </Link>
                      ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-x-2.5 border-t pt-5">
              <Button
                asChild
                className="min-w-0 flex-1"
                size="lg"
                variant="outline"
              >
                <Link to="/investments/direct">
                  <ArrowRightIcon className="shrink-0" />
                  <span className="truncate">Go to Opportunities</span>
                </Link>
              </Button>
              <Button
                className="min-w-0 flex-1"
                onClick={() => {
                  void navigate('/investments/direct');
                }}
                size="lg"
              >
                <PlusIcon className="shrink-0" />
                <span className="truncate">Add Opportunity</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Views Section */}
        <div className="rounded-md border bg-white px-2.5 py-5">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-x-1.25">
              <EyeIcon className="size-6" />
              <h2 className="text-lg font-semibold">Views</h2>
            </div>
            <div className="text-muted-foreground flex items-center gap-x-1">
              <span className="text-sm font-medium">
                {viewsData?.userSavedViews.edges.length.toLocaleString() ?? '0'}
              </span>
              <InfoIcon className="size-4.5" />
            </div>
          </div>

          <InputGroup className="mb-2.5">
            <InputGroupAddon>
              <SearchIcon />
            </InputGroupAddon>
            <InputGroupInput
              onChange={e => {
                setViewsSearch(e.target.value);
              }}
              placeholder="Search views..."
              value={viewsSearch}
            />
          </InputGroup>

          {viewsError ? (
            <div className="text-sm text-red-500">Error loading views</div>
          ) : (
            <div className="space-y-4">
              {Object.entries(viewsGrouped).map(([topLevelKey, nested]) => {
                const isNoDepartment = topLevelKey.startsWith('_nodept_');
                const displayName = isNoDepartment
                  ? topLevelKey
                      .replace('_nodept_', '')
                      .replace('_no_category', 'Uncategorized')
                  : topLevelKey;

                const totalViews = Object.values(nested).reduce(
                  (sum, views) => sum + views.length,
                  0
                );

                // If no department, render views directly (no nesting)
                if (isNoDepartment) {
                  const views = nested._direct || [];
                  return (
                    <div
                      className="rounded-md border"
                      key={topLevelKey}
                    >
                      <button
                        className="flex w-full items-center justify-between px-2.5 py-5 text-left hover:bg-gray-50"
                        onClick={() => {
                          toggleViewCategory(topLevelKey);
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-semibold">{displayName}</span>
                          <Badge variant="secondary">{views.length}</Badge>
                        </div>
                        <ChevronDownIcon
                          className={cn(
                            'size-4 transition-transform',
                            expandedViewCategories.has(topLevelKey) &&
                              'rotate-180'
                          )}
                        />
                      </button>

                      {expandedViewCategories.has(topLevelKey) && (
                        <div className="space-y-0">
                          {views.map(view => (
                            <Link
                              className="flex items-center justify-between border-t px-8 py-3 hover:bg-gray-50"
                              key={view.id}
                              to={`/firm/crm-${view.targetModel === 'account' ? 'accounts' : 'contacts'}?viewId=${view.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <MoveDiagonalIcon className="size-4" />
                                <div className="flex items-center gap-3">
                                  <Badge variant="outline">
                                    {view.targetModel === 'account' ? (
                                      <>
                                        <BuildingIcon className="size-3" />
                                        Account
                                      </>
                                    ) : (
                                      <>
                                        <UsersIcon className="size-3" />
                                        Contact
                                      </>
                                    )}
                                  </Badge>
                                  {view.name && view.name.length > 50 ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="block max-w-[300px] cursor-default truncate text-sm text-gray-600">
                                          {view.name}
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent className="max-w-xs">
                                        {view.name}
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : (
                                    <span className="text-sm text-gray-600">
                                      {view.name || 'Unnamed View'}
                                    </span>
                                  )}
                                  <Badge variant="secondary">
                                    {view.recordCount}
                                  </Badge>
                                </div>
                              </div>
                              <div className="flex items-center gap-4 text-xs">
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-500">
                                    Created By
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <Avatar className="size-6">
                                      <AvatarFallback className="text-xs">
                                        {getInitials(view.createdByName)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span className="font-medium">
                                      {view.createdByName}
                                    </span>
                                  </div>
                                </div>
                                <Separator
                                  className="h-5"
                                  orientation="vertical"
                                />
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-500">
                                    Created At
                                  </span>
                                  <span className="font-medium">
                                    {new Date(
                                      view.createdAt
                                    ).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                // Has department: Show Department -> Categories -> Views
                return (
                  <div
                    className="rounded-md border"
                    key={topLevelKey}
                  >
                    <button
                      className="flex w-full items-center justify-between px-2.5 py-5 text-left hover:bg-gray-50"
                      onClick={() => {
                        toggleViewDepartment(topLevelKey);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-semibold">{displayName}</span>
                        <Badge variant="secondary">{totalViews}</Badge>
                      </div>
                      <ChevronDownIcon
                        className={cn(
                          'size-4 transition-transform',
                          expandedViewDepartments.has(topLevelKey) &&
                            'rotate-180'
                        )}
                      />
                    </button>

                    {expandedViewDepartments.has(topLevelKey) && (
                      <div className="space-y-2.5 p-2.5">
                        {Object.entries(nested).map(([category, views]) => {
                          const catDisplay =
                            category === '_no_category'
                              ? 'Uncategorized'
                              : category;
                          if (category === '_no_category') {
                            // Render views without category directly
                            return (
                              <div key={category}>
                                {views.map(view => (
                                  <Link
                                    className="flex items-center justify-between border-b px-8 py-3 last:border-b-0 hover:bg-gray-50"
                                    key={view.id}
                                    to={`/firm/crm-${view.targetModel === 'account' ? 'accounts' : 'contacts'}?viewId=${view.id}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <MoveDiagonalIcon className="size-4" />
                                      <div className="flex items-center gap-3">
                                        <Badge variant="outline">
                                          {view.targetModel === 'account' ? (
                                            <>
                                              <BuildingIcon className="size-3" />
                                              Account
                                            </>
                                          ) : (
                                            <>
                                              <UsersIcon className="size-3" />
                                              Contact
                                            </>
                                          )}
                                        </Badge>
                                        <span className="text-sm text-gray-600">
                                          {view.name || 'Unnamed View'}
                                        </span>
                                        <Badge variant="secondary">
                                          {view.recordCount}
                                        </Badge>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-4 text-xs">
                                      <div className="flex items-center gap-2">
                                        <span className="text-gray-500">
                                          Created By
                                        </span>
                                        <div className="flex items-center gap-2">
                                          <Avatar className="size-6">
                                            <AvatarFallback className="text-xs">
                                              {getInitials(view.createdByName)}
                                            </AvatarFallback>
                                          </Avatar>
                                          <span className="font-medium">
                                            {view.createdByName}
                                          </span>
                                        </div>
                                      </div>
                                      <Separator
                                        className="h-5"
                                        orientation="vertical"
                                      />
                                      <div className="flex items-center gap-2">
                                        <span className="text-gray-500">
                                          Created At
                                        </span>
                                        <span className="font-medium">
                                          {new Date(
                                            view.createdAt
                                          ).toLocaleDateString()}
                                        </span>
                                      </div>
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            );
                          }

                          // Render category with nested views
                          const catKey = `${topLevelKey}_${category}`;
                          return (
                            <div
                              className="rounded-md border"
                              key={category}
                            >
                              <button
                                className="flex w-full items-center justify-between px-2.5 py-3 text-left text-sm hover:bg-gray-50"
                                onClick={() => {
                                  toggleViewCategory(catKey);
                                }}
                              >
                                <span className="font-medium">
                                  {catDisplay}
                                </span>
                                <ChevronDownIcon
                                  className={cn(
                                    'size-4 transition-transform',
                                    expandedViewCategories.has(catKey) &&
                                      'rotate-180'
                                  )}
                                />
                              </button>

                              {expandedViewCategories.has(catKey) && (
                                <div className="relative">
                                  <div className="bg-border absolute top-2.5 left-5 h-[calc(100%-20px)] w-0.25" />
                                  {views.map(view => (
                                    <Link
                                      className="flex items-center justify-between px-8 py-3 hover:bg-gray-50"
                                      key={view.id}
                                      to={`/firm/crm-${view.targetModel === 'account' ? 'accounts' : 'contacts'}?viewId=${view.id}`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <MoveDiagonalIcon className="size-4" />
                                        <div className="flex items-center gap-3">
                                          <Badge variant="outline">
                                            {view.targetModel === 'account' ? (
                                              <>
                                                <BuildingIcon className="size-3" />
                                                Account
                                              </>
                                            ) : (
                                              <>
                                                <UsersIcon className="size-3" />
                                                Contact
                                              </>
                                            )}
                                          </Badge>
                                          <span className="text-sm text-gray-600">
                                            {view.name || 'Unnamed View'}
                                          </span>
                                          <Badge variant="secondary">
                                            {view.recordCount}
                                          </Badge>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-4 text-xs">
                                        <div className="flex items-center gap-2">
                                          <span className="text-gray-500">
                                            Created By
                                          </span>
                                          <div className="flex items-center gap-2">
                                            <Avatar className="size-6">
                                              <AvatarFallback className="text-xs">
                                                {getInitials(
                                                  view.createdByName
                                                )}
                                              </AvatarFallback>
                                            </Avatar>
                                            <span className="font-medium">
                                              {view.createdByName}
                                            </span>
                                          </div>
                                        </div>
                                        <Separator
                                          className="h-5"
                                          orientation="vertical"
                                        />
                                        <div className="flex items-center gap-2">
                                          <span className="text-gray-500">
                                            Created At
                                          </span>
                                          <span className="font-medium">
                                            {new Date(
                                              view.createdAt
                                            ).toLocaleDateString()}
                                          </span>
                                        </div>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <CreateContactDialog
        isOpen={isCreateContactDialogOpen}
        onOpenChange={setIsCreateContactDialogOpen}
        refetchQuery={{
          query: GET_CONTACTS_SUMMARY,
          variables: { first: 3 }
        }}
      />
      <CreateAccountDialog
        isOpen={isCreateAccountDialogOpen}
        onOpenChange={setIsCreateAccountDialogOpen}
        refetchQuery={{
          query: GET_ACCOUNTS_SUMMARY,
          variables: { first: 3 }
        }}
      />
    </div>
  );
};

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default Home;
